import type { ToggleName } from '@tilelist/domain/experiments/Toggles.ts';

declare global {
    namespace App {
        interface Onex {
            DUMMY: 'groupA' | 'groupB';
            DUMMY_N_GROUP: 'groupA' | 'groupB' | 'groupC';
            E3772: 'StatusQuo' | 'Testgroup';
            E3822: 'E3822A' | 'E3822B';
            E3917: 'E3917A' | 'E3917B';
            E3763: 'StatusQuo' | 'Positions';
            E3886: 'E3886A' | 'E3886B';
            E3803: 'E3803A' | 'E3803B' | 'E3803C';
            E3892: 'E3892A' | 'E3892B' | 'E3892C';
            E3738: 'E3738A' | 'E3738B';
            E3946: 'E3946A' | 'E3946B';
            E3949: 'E3949A' | 'E3949B' | 'E3949C';
            E3951: 'E3951A' | 'E3951B';
            E3933: 'E3933A' | 'E3933B';
            E3970: 'E3970A' | 'E3970B';
        }
    }
}

type OnexExperimentId = keyof App.Onex;
type OnexGroup<Id extends OnexExperimentId> = App.Onex[Id];
export type OnexAssignments = Partial<{ [K in OnexExperimentId]: App.Onex[K] }>;

/**
 *
 */
export const EXPERIMENT_ONEX_IDS = {
    DUMMY: 'DUMMY',
    DUMMY_N_GROUP: 'DUMMY_N_GROUP',
    TEASER_VARIATION_BOOST: 'E3772',
    GALLERY_LANDSCAPE_TWO_COLUMN: 'E3822',
    ARRANGEMENT_LIST_ELEMENTS_ON_TOP: 'E3763',
    DELIVERY_DATE: 'E3917',
    SPA_VIA_SEARCH: 'E3886',
    WISHLIST_ELEMENT: 'E3803',
    PRICING_COMPONENT: 'E3892',
    LOAD_MORE_TOPICPAGE: 'E3738',
    TOP_INFO_SCANABILITY: 'E3946',
    INSTALLMENT_WARNING: 'E3949',
    ENTRY_PAGE: 'E3951',
    VISUAL_REFRESH: 'E3933',
    SOCIAL_PROOF_OUTSIDE_IMAGE: 'E3970',
} as const satisfies Record<string, OnexExperimentId>;

export type ExperimentId = keyof typeof EXPERIMENT_ONEX_IDS;

export type ExperimentOnexId = (typeof EXPERIMENT_ONEX_IDS)[ExperimentId];

export type ExperimentGroups = {
    [K in ExperimentId]: OnexGroup<(typeof EXPERIMENT_ONEX_IDS)[K]>;
};

/**
 *
 */
export interface ExperimentConfig<E extends keyof ExperimentGroups> {
    /**
 *
 */
    onexExperimentId: (typeof EXPERIMENT_ONEX_IDS)[E];

    /**
 *
 *
 *
 */
    experimentToggle: ToggleName;

    /**
 *
 *
 *
 *
 */
    featureToggle: ToggleName;

    /**
 *
 *
 */
    groups: ReadonlyArray<ExperimentGroups[E]>;

    /**
 *
 *
 */
    defaultGroup: ExperimentGroups[E];

    /**
 *
 *
 */
    featureOnGroup: ExperimentGroups[E];
}

export type ExperimentConfigs = {
    [K in keyof ExperimentGroups]: ExperimentConfig<K>;
};

/**
 *
 *
 *
 *
 *
 *
 *
 */
export const EXPERIMENTS = {
    DUMMY: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.DUMMY,
        experimentToggle: 'EXPERIMENT_DUMMY_ACTIVE',
        featureToggle: 'FEATURE_DUMMY_USE_GROUP_A',
        groups: ['groupA', 'groupB'],
        defaultGroup: 'groupB',
        featureOnGroup: 'groupA',
    },
    DUMMY_N_GROUP: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.DUMMY_N_GROUP,
        experimentToggle: 'EXPERIMENT_DUMMY_N_GROUP_ACTIVE',
        featureToggle: 'FEATURE_DUMMY_N_GROUP_USE_GROUP_B',
        groups: ['groupA', 'groupB', 'groupC'],
        defaultGroup: 'groupA',
        featureOnGroup: 'groupB',
    },
    TEASER_VARIATION_BOOST: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.TEASER_VARIATION_BOOST,
        experimentToggle: 'REPTILE_1564_BOOST_TEASER_VARIATIONS_EXPERIMENT',
        featureToggle: 'REPTILE_1564_BOOST_TEASER_VARIATIONS',
        groups: ['StatusQuo', 'Testgroup'],
        defaultGroup: 'StatusQuo',
        featureOnGroup: 'Testgroup',
    },
    GALLERY_LANDSCAPE_TWO_COLUMN: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.GALLERY_LANDSCAPE_TWO_COLUMN,
        experimentToggle: 'REPTILE_1590_TWO_COLUMN_LANDSCAPE_EXPERIMENT',
        featureToggle: 'REPTILE_1590_TWO_COLUMN_LANDSCAPE',
        groups: ['E3822A', 'E3822B'],
        defaultGroup: 'E3822A',
        featureOnGroup: 'E3822B',
    },
    ARRANGEMENT_LIST_ELEMENTS_ON_TOP: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.ARRANGEMENT_LIST_ELEMENTS_ON_TOP,
        experimentToggle: 'REPTILE_1288_ARRANGEMENT_EXPERIMENT',
        featureToggle: 'REPTILE_1288_ARRANGEMENT',
        groups: ['StatusQuo', 'Positions'],
        defaultGroup: 'StatusQuo',
        featureOnGroup: 'Positions',
    },
    DELIVERY_DATE: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.DELIVERY_DATE,
        experimentToggle: 'REPTILE_1639_DELIVERY_DATE_EXPERIMENT',
        featureToggle: 'REPTILE_1639_DELIVERY_DATE',
        groups: ['E3917A', 'E3917B'],
        defaultGroup: 'E3917A',
        featureOnGroup: 'E3917B',
    },
    SPA_VIA_SEARCH: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.SPA_VIA_SEARCH,
        experimentToggle: 'REPTILE_1628_SPA_VIA_SEARCH_EXPERIMENT',
        featureToggle: 'REPTILE_1628_SPA_VIA_SEARCH',
        groups: ['E3886A', 'E3886B'],
        defaultGroup: 'E3886A',
        featureOnGroup: 'E3886B',
    },
    INSTALLMENT_WARNING: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.INSTALLMENT_WARNING,
        experimentToggle: 'REPTILE_1624_INSTALLMENT_WARNING_EXPERIMENT',
        featureToggle: 'REPTILE_1624_INSTALLMENT_WARNING',
        groups: ['E3949A', 'E3949B', 'E3949C'],
        defaultGroup: 'E3949A',
        featureOnGroup: 'E3949B',
    },
    WISHLIST_ELEMENT: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.WISHLIST_ELEMENT,
        experimentToggle: 'REPTILE_1663_WISHLIST_ELEMENT_EXPERIMENT',
        featureToggle: 'REPTILE_1663_WISHLIST_ELEMENT',
        groups: ['E3803A', 'E3803B', 'E3803C'],
        defaultGroup: 'E3803A',
        featureOnGroup: 'E3803B',
    },
    PRICING_COMPONENT: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.PRICING_COMPONENT,
        experimentToggle: 'REPTILE_1643_PRICING_COMPONENT_EXPERIMENT',
        featureToggle: 'REPTILE_1643_PRICING_COMPONENT',
        groups: ['E3892A', 'E3892B', 'E3892C'],
        defaultGroup: 'E3892A',
        featureOnGroup: 'E3892B',
    },
    LOAD_MORE_TOPICPAGE: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.LOAD_MORE_TOPICPAGE,
        experimentToggle: 'REPTILE_1492_LOAD_MORE_TOPICPAGE_EXPERIMENT',
        featureToggle: 'REPTILE_1492_LOAD_MORE_TOPICPAGE',
        groups: ['E3738A', 'E3738B'],
        defaultGroup: 'E3738A',
        featureOnGroup: 'E3738B',
    },
    TOP_INFO_SCANABILITY: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.TOP_INFO_SCANABILITY,
        experimentToggle: 'REPTILE_1731_TOP_INFO_SCANABILITY_EXPERIMENT',
        featureToggle: 'REPTILE_1731_TOP_INFO_SCANABILITY',
        groups: ['E3946A', 'E3946B'],
        defaultGroup: 'E3946A',
        featureOnGroup: 'E3946B',
    },
    ENTRY_PAGE: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.ENTRY_PAGE,
        experimentToggle: 'REPTILE_1736_ENTRY_PAGE_SIDEBAR_EXPERIMENT',
        featureToggle: 'REPTILE_1736_HIDE_ENTRY_PAGE_SIDEBAR',
        groups: ['E3951A', 'E3951B'],
        defaultGroup: 'E3951A',
        featureOnGroup: 'E3951B',
    },
    VISUAL_REFRESH: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.VISUAL_REFRESH,
        experimentToggle: 'REPTILE_1710_VISUAL_REFRESH_EXPERIMENT',
        featureToggle: 'REPTILE_1710_VISUAL_REFRESH',
        groups: ['E3933A', 'E3933B'],
        defaultGroup: 'E3933A',
        featureOnGroup: 'E3933B',
    },
    SOCIAL_PROOF_OUTSIDE_IMAGE: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.SOCIAL_PROOF_OUTSIDE_IMAGE,
        experimentToggle: 'REPTILE_1756_SOCIAL_PROOF_OUTSIDE_IMAGE_EXPERIMENT',
        featureToggle: 'REPTILE_1756_SOCIAL_PROOF_OUTSIDE_IMAGE',
        groups: ['E3970A', 'E3970B'],
        defaultGroup: 'E3970A',
        featureOnGroup: 'E3970B',
    },
} as const satisfies ExperimentConfigs;
